/* eslint-disable */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { useAuth0 } from "@auth0/auth0-react";
import { useSearchParams } from "react-router-dom";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { ReactComponent as IconEye } from "@assets/Icons/Icon_Eye.svg";
import {
  getSupportTickets,
  getSupportTicketById,
  getKontrolSupportTickets,
  getSupportKontrolTicketById,
  postReplyTicket,
  downloadAttachment,
} from "./request/request";
import {
  getStatusText,
  getTicketColumns,
  getTicketTableMinWidthClass,
  SupportTicket,
} from "./RequestData";
import { TicketDetail } from "./TicketDetail";
import ChangeRequestModal from "./components/ChangeRequestModal";
import IncidentReportModal from "./components/IncidentReportModal";
import FeatureRequestModal from "./components/FeatureRequestModal";
import HelpRequestModal from "./components/HelpRequestModal";
import RequestTypeCards from "./components/requestForm/RequestTypeCards";
import type { RequestTypeId } from "./components/requestForm/requestTypes";
import GenericCard from "@ui-components/Card/GenericCard";
import ListPageLayout from "@ui-components/ListPage/ListPageLayout";
import ListPageToolbar from "@ui-components/ListPage/ListPageToolbar";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
  buildPredicate,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import {
  displayStatus,
  isWaitingForCustomer,
  makeSupportFields,
  matchesTicketPhase,
  shortTicketType,
  TICKET_PHASES,
  TicketPhase,
  TicketPriorityTag,
  TicketStatusTag,
  TicketTypeTag,
  timeAgo,
} from "./SupportFields";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import { useWindowSize } from "@shared/hooks/useWindowSize";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import TicketDetailsModal from "./components/TicketDetailsModal";
import { TICKET_LINK_PARAM, findLinkedTicket } from "./utils/linkedTicket";

export function RequestPage() {
  const { isAuthenticated, isLoading } = useAuth0();
  const api = useAxiosAuth0();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const tenantType = useAppSelector(
    (s) =>
      s.userProfile.TenantType as
        | "GLOBAL_ADMIN"
        | "PARTNER"
        | "CUSTOMER"
        | undefined
  );
  const shouldPassTenantId =
    tenantType !== "CUSTOMER" && Number.isFinite(tenantId);

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | number>("");
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [hasLoadedTickets, setHasLoadedTickets] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedTicket = searchParams.get(TICKET_LINK_PARAM);

  // The request modal that is open, if any; only one at a time.
  const [openRequestType, setOpenRequestType] = useState<RequestTypeId | null>(
    null
  );
  const closeRequestModal = () => setOpenRequestType(null);
  const [replyText, setReplyText] = useState("");
  const [isReplyLoading, setIsReplyLoading] = useState(false);
  const [isDownloadingAttachment, setIsDownloadingAttachment] = useState(false);

  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const isViewingAllCustomers = tenantId === partnerId;
  // Check if the selected product is "Kontrol Edge"
  const globalProduct = useAppSelector(
    (state) => state.product.selectedProduct
  );
  const isKontrolEdge = globalProduct === "Kontrol Edge";
  const ticketsColumns = getTicketColumns(globalProduct);

  const fetchTickets = useCallback(
    async (signal?: AbortSignal) => {
      if (isLoading) return;
      if (!isAuthenticated) {
        console.warn("User not authenticated; skipping fetch.");
        return;
      }
      if (!tenantId) {
        console.log("No tenantId yet; skipping fetch...");
        return;
      }

      setIsFetching(true);
      try {
        let shaped: SupportTicket[] = [];

        if (isKontrolEdge) {
          const resp = await getKontrolSupportTickets(api, {
            tenantId: shouldPassTenantId ? tenantId : undefined,
            signal,
          });
          shaped = resp.data.tickets.map((item: any) => ({
            ...item,
            id: item.id,
            key: item.key,
            type: item.type,
            subject: item.summary,
            priority: item.priority,
            status: item.status,
          }));
        } else {
          const resp = await getSupportTickets(api, tenantId);
          shaped = (resp.data as SupportTicket[]).map((item: any) => {
            // Ensure id is properly set for CTX Reflector ticket number display
            const ticketId = item.id ?? item._id ?? item.ticketId ?? "";
            return {
              ...item,
              id: ticketId ? String(ticketId) : "",
              key: item.key || ticketId || "",
              type: item.type || "",
              subject: item.subject || "",
              priority: item.priority ?? 0,
              status: item.status || {},
            };
          });
        }

        if (signal?.aborted) return; // bail if canceled mid-flight
        setTickets(shaped);
      } catch (err: any) {
        if (err?.name !== "CanceledError" && err?.message !== "canceled") {
          console.error("Error fetching tickets =>", err);
        }
      } finally {
        if (!signal?.aborted) {
          setIsFetching(false);
          setHasLoadedTickets(true);
        }
      }
    },
    [api, tenantId, isKontrolEdge, isAuthenticated, isLoading]
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchTickets(controller.signal);
    return () => controller.abort();
  }, [fetchTickets]);

  // The number and type the list shows; the detail endpoints carry neither.
  const selectedTicketSummary = useMemo(() => {
    const row = tickets.find((t) => t.id === selectedTicketId);
    if (!row) return undefined;
    return {
      number: globalProduct === "CTX Reflector" ? row.id : row.key,
      type: row.type || undefined,
    };
  }, [tickets, selectedTicketId, globalProduct]);

  const handleSeeDetails = (ticketId: string | number) => {
    setSelectedTicketId(ticketId);
    setIsDetailsOpen(true);
  };

  // A link to one ticket (`?ticket=KS-1660`, from the participant emails)
  // opens its detail once the list is in, so the header gets the number and
  // type the list knows. A ticket missing from the list still opens by its
  // number: the detail endpoint decides whether the user may see it.
  useEffect(() => {
    if (!linkedTicket || !hasLoadedTickets) return;
    const row = findLinkedTicket(tickets, linkedTicket);
    setSelectedTicketId(row ? row.id : linkedTicket);
    setIsDetailsOpen(true);
  }, [linkedTicket, hasLoadedTickets, tickets]);

  // Closing drops the link from the URL, so a reload does not reopen it.
  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    if (!searchParams.has(TICKET_LINK_PARAM)) return;
    const next = new URLSearchParams(searchParams);
    next.delete(TICKET_LINK_PARAM);
    setSearchParams(next, { replace: true });
  };

  // Provide a uniform fetcher for the modal
  const fetchTicketById = useCallback(
    async (ticketId: string | number): Promise<TicketDetail> => {
      const resp = isKontrolEdge
        ? await getSupportKontrolTicketById(api, ticketId)
        : await getSupportTicketById(api, ticketId);

      // Who wrote each reply is worked out by the conversation itself
      // (getConversationEntry), from the author or the text's prefix.
      return {
        ...resp.data,
        conversations: resp.data.conversations || [],
      } as TicketDetail;
    },
    [api, isKontrolEdge]
  );

  // -------------------------------
  // Handle sending a reply
  // -------------------------------
  const handleSendReply = useCallback(
    async (
      id: string | number,
      msg: string,
      files: File[] = []
    ): Promise<void> => {
      if (id == null) return;
      try {
        setIsReplyLoading(true);
        await postReplyTicket(api, id, { comment: msg.trim(), files });
        // No refetch here; the modal will do it
        setReplyText("");
        setReplyAttachments([]);
      } finally {
        setIsReplyLoading(false);
      }
    },
    [api]
  );

  const handleDownloadAttachment = async (
    ticketId: string | number,
    attachment: { id: string; filename?: string; name?: string }
  ) => {
    try {
      setIsDownloadingAttachment(true);
      const blob = await downloadAttachment(api, ticketId, attachment.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const filename = attachment.filename || attachment.name || "attachment";
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading attachment =>", err);
    } finally {
      setIsDownloadingAttachment(false);
    }
  };

  const supportFields = useMemo(
    () => makeSupportFields(isViewingAllCustomers, isKontrolEdge),
    [isViewingAllCustomers, isKontrolEdge]
  );

  const suggestedKeys = useMemo(
    () => supportFields.map((f) => f.label),
    [supportFields]
  );

  const normalized = useMemo(
    () =>
      normalizeFilters<SupportTicket>(activeFilters, supportFields, {
        defaultStringToContains: true,
        quotedStringsExact: true,
      }),
    [activeFilters, supportFields]
  );

  const predicate = useMemo(
    () => buildPredicate<SupportTicket>(normalized, supportFields),
    [normalized, supportFields]
  );

  // A phone filters by Open / Waiting for you / Closed and opens on Open; the
  // table keeps every ticket and its column sort on wider screens (CTX-1433).
  const isPhone = useWindowSize().width < 640;
  const [phase, setPhase] = useState<TicketPhase>("Open");

  const searchedTickets = useMemo(
    () => tickets.filter(predicate),
    [tickets, predicate]
  );

  const phaseCounts = useMemo(
    () =>
      Object.fromEntries(
        TICKET_PHASES.map((p) => [
          p,
          searchedTickets.filter((t) => matchesTicketPhase(t.status, p)).length,
        ])
      ) as Record<TicketPhase, number>,
    [searchedTickets]
  );

  const filteredData = useMemo(
    () =>
      isPhone
        ? searchedTickets.filter((t) => matchesTicketPhase(t.status, phase))
        : searchedTickets,
    [searchedTickets, isPhone, phase]
  );

  // A phone card in three lines: number and type, the title, then status and
  // age. Reporter and services stay in the detail.
  const phoneActivityColumn: GenericTableColumn<SupportTicket> = {
    header: "Activity",
    renderCell: (row) => {
      const waiting = isWaitingForCustomer(row.status);
      const status = waiting ? "Waiting for you" : displayStatus(row.status);
      return (
        <span className="text-xs text-semantic-text-secondary">
          <span
            className={
              waiting ? "font-semibold text-semantic-text-warning" : undefined
            }
          >
            {status}
          </span>
          {row.updatedAt && ` · ${timeAgo(row.updatedAt)}`}
        </span>
      );
    },
  };

  return (
    <ListPageLayout>
      <div className="mb-2 flex-shrink-0 sm:mb-4">
        <RequestTypeCards onSelect={setOpenRequestType} />
      </div>

      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">
              <ListPageToolbar
                className="w-full max-w-full sm:max-w-[520px]"
                footer={
                  isPhone && (
                    <GenericTabs
                      tabs={TICKET_PHASES.map((p) => ({
                        label: `${p} ${phaseCounts[p]}`,
                        value: p,
                      }))}
                      activeTab={phase}
                      onTabChange={(value) => setPhase(value as TicketPhase)}
                      size="xs"
                      fullWidth={false}
                      fitContent
                      aria-label="Ticket status"
                    />
                  )
                }
              >
                <GenericSearchbar
                  placeholder="Find by attribute or value"
                  suggestedKeys={suggestedKeys}
                  onFiltersChange={setActiveFilters}
                  liveFiltering
                  debounceMs={200}
                />
              </ListPageToolbar>

              <div className="flex-1 min-h-0 flex flex-col">
                {/* The table's own slot in skeleton. The block this replaces was
                    a hand-copied full-screen overlay (CTX-1124). */}
                {isFetching ? (
                  <TableSkeleton columns={ticketsColumns.length} rows={5} />
                ) : (
                  <GenericTable
                    mobileCards={{
                      titleColumn: "Ticket N°",
                      badgeColumn: "Priority",
                      headlineColumn: "Title",
                      headlineLines: 2,
                      fieldColumns: [],
                      footerColumns: ["Activity"],
                      opensFromTitle: true,
                    }}
                    columns={[
                      ...ticketsColumns,
                      ...(isPhone ? [phoneActivityColumn] : []),
                    ].map((col) => {
                      if (col.header === "Type") {
                        return {
                          ...col,
                          renderCell: (row: SupportTicket) =>
                            row.type ? <TicketTypeTag value={row.type} /> : "—",
                        };
                      }
                      if (col.header === "Ticket N°") {
                        return {
                          ...col,
                          renderCell: (row: SupportTicket) => {
                            const ticketNumber = globalProduct === "CTX Reflector" ? row.id : row.key;
                            const phoneType = isPhone ? shortTicketType(row.type) : "";
                            return (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSeeDetails(row.id);
                                }}
                                className="
                                  text-semantic-text-brand
                                  font-bold
                                  underline
                                  hover:opacity-80
                                  cursor-pointer
                                  text-left
                                "
                              >
                                {ticketNumber || "—"}
                                {phoneType && (
                                  <span className="inline-block font-normal text-semantic-text-secondary no-underline">
                                    {` · ${phoneType}`}
                                  </span>
                                )}
                              </button>
                            );
                          },
                        } as GenericTableColumn<SupportTicket>;
                      }
                      if (col.header === "Status") {
                        return {
                          ...col,
                          renderCell: (row: SupportTicket) => {
                            const st = getStatusText(row.status);
                            return st !== "—" ? (
                              <TicketStatusTag value={st} />
                            ) : (
                              "—"
                            );
                          },
                        };
                      }
                      if (col.header === "Priority") {
                        return {
                          ...col,
                          renderCell: (row: SupportTicket) =>
                            Number.isFinite(row.priority) ? (
                              <TicketPriorityTag value={Number(row.priority)} />
                            ) : (
                              "—"
                            ),
                        };
                      }
                      if (col.header === "Details") {
                        return {
                          ...col,
                          renderCell: (row: SupportTicket) => (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSeeDetails(row.id);
                              }}
                              className="
                    flex items-center justify-center
                    border border-semantic-border-secondary
                    rounded
                    text-semantic-text-secondary
                    hover:bg-semantic-state-active
                    w-10 h-10
                  "
                            >
                              <IconEye className="w-6 h-6" />
                            </button>
                          ),
                        } as GenericTableColumn<SupportTicket>;
                      }
                      return col;
                    })}
                    data={filteredData}
                    initialRowsPerPage={10}
                    rowsPerPageOptions={[10, 20, 30]}
                    width="100%"
                    tableClassName={`bg-semantic-bg-primary ${getTicketTableMinWidthClass(globalProduct)}`}
                    maxTableBodyHeight="100%"
                  />
                )}
              </div>
            </div>
          }
        />
      </div>

      <TicketDetailsModal
        isOpen={isDetailsOpen}
        onClose={handleCloseDetails}
        ticketId={selectedTicketId}
        summary={selectedTicketSummary}
        fetchTicketById={fetchTicketById}
        onDownloadAttachment={handleDownloadAttachment}
        onSendReply={handleSendReply}
        isKontrolEdge={isKontrolEdge}
      />

      <ChangeRequestModal
        isOpen={openRequestType === "change"}
        onClose={closeRequestModal}
        onTicketCreated={fetchTickets}
      />
      <IncidentReportModal
        isOpen={openRequestType === "incident"}
        onClose={closeRequestModal}
        onTicketCreated={fetchTickets}
      />
      <FeatureRequestModal
        isOpen={openRequestType === "feature"}
        onClose={closeRequestModal}
        onTicketCreated={fetchTickets}
      />
      <HelpRequestModal
        isOpen={openRequestType === "help"}
        onClose={closeRequestModal}
        onTicketCreated={fetchTickets}
      />
    </ListPageLayout>
  );
}

export default RequestPage;
